import gql from 'graphql-tag';
import { PEOPLE_COMPANY_DOCUMENT_FRAGMENT } from './companyDocument';

export const PEOPLE_EMPLOYEE_ONBOARDING_INVITE_HISTORY_FRAGMENT = gql`
	fragment peopleEmployeeOnboardingInviteHistory on PeopleEmployeeOnboardingInviteHistoryItem {
		id
		created
		emailSentAt
		sentTo
		expiresAt
		usedAt
		invalidatedAt
		generatedBy {
			id
			firstname
			lastname
		}
	}
`;

export const PEOPLE_EMPLOYEE_EMAIL_FOLLOW_UP_HISTORY_FRAGMENT = gql`
	fragment peopleEmployeeEmailFollowUpHistory on PeopleEmployeeEmailFollowUpHistoryItem {
		id
		reminderType
		item
		sentAt
		sentTo
	}
`;

export const PEOPLE_EMPLOYEE_LIST_FRAGMENT = gql`
	fragment peopleEmployeeList on Employee {
		id
		status
		hiddenFromRoster
		actionsSummary {
			level
			intent
			label
			summary
			count
			reasons
			details
		}
		onboardingActionsSummary {
			level
			intent
			label
			summary
			count
			reasons
			details
		}
		ongoingActionsSummary {
			level
			intent
			label
			summary
			count
			reasons
			details
		}
		adminActionsSummary {
			level
			intent
			label
			summary
			count
			reasons
			details
		}
		firstName
		lastName
		preferredName
		personalEmail
		workEmail
		workPhone
		emailSignatureAutomaticRegards
		emailSignatureSignOff
		phoneNumber
		address
		position {
			id
			name
			eventsRequireReview
		}
		team
		employmentType
		startDate
		originalStartDate
		dateOfBirth
		onboardedAt
		onboardingEmailSentAt
		terminationDate
		orgChartOrder
		managerEmployee {
			id
			firstName
			lastName
			preferredName
		}
		managerEmployeeId
		workTenant {
			id
			name
		}
		workDays
		linkAccessGrantedAt
		clientsAccessGrantedAt
		deputyEmployeeId
		deputyDisplayName
		defaultUnpaidBreakMinutes
	}
`;

export const PEOPLE_EMPLOYEE_DETAIL_FRAGMENT = gql`
	fragment peopleEmployeeDetail on Employee {
		...peopleEmployeeList

		sentryAppRole {
			id
			name
		}

		user {
			id
			firstname
			lastname
			username
			destroyed
			enabled
			deputyEmployeeId
			deputyDisplayName
			defaultUnpaidBreakMinutes
			role {
				id
				name
				permissions
			}
		}

			hasLinkAccess
			hasClientsAccess
			pendingLinkAccessRole {
				id
				name
			}
			pendingClientsAccessRole {
				id
				name
			}

			address
			dateOfBirth
		gender
		emergencyContact
		profilePhotoUrl
		profilePrompts
		receiveRosterChangeEmails
		includeInBirthdayAnnouncements
		includeInAnniversaryAnnouncements

		awardName
		positionDescriptionDocument {
			...peopleCompanyDocument
		}
		workTenant {
			id
			name
			masterTenant {
				id
				tenantSettings {
					peopleDefaultSuperannuationFund
				}
			}
			tenantSettings {
				peopleDefaultSuperannuationFund
			}
		}
		team
		hoursPerWeek
		workDays
		salary
		originalStartDate
		managerEmployee {
			id
			firstName
			lastName
			preferredName
			personalEmail
			position {
				id
				name
				eventsRequireReview
			}
		}
		managerEmployeeId
		probationEndDate
		leaveEntitlements
		autoApproveLeaveRequests
		emailAutomaticallyApprovedRequests

		payrollId
		excludeFromXeroTimesheetExport
		pay
		superannuation
		banking
		tax

		terminationDate
		terminationReason
		terminationDetails
		reengagementEligible
		serviceBreaks

		onboardingChecklist
		onboardingResponseOverview
		offboardingChecklist
	}
	${PEOPLE_EMPLOYEE_LIST_FRAGMENT}
	${PEOPLE_COMPANY_DOCUMENT_FRAGMENT}
`;

export const GET_EMPLOYEES = gql`
	query(
		$page: Int,
		$perPage: Int,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: EmployeeDirectoryFilter,
		$includeOnboarding: Boolean = false,
		$includeOffboarding: Boolean = false,
		$includeResponseOverview: Boolean = false,
		$includeTrainingMatrix: Boolean = false,
		$includeOnboardingInviteHistory: Boolean = false,
		$includeEmailFollowUpHistory: Boolean = false,
		$includeProfileAboutYou: Boolean = false
	) {
		getEmployees(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...peopleEmployeeList
				profilePhotoUrl @include(if: $includeProfileAboutYou)
				profilePrompts @include(if: $includeProfileAboutYou)
				onboardingChecklist @include(if: $includeOnboarding)
				terminationReason @include(if: $includeOffboarding)
				offboardingChecklist @include(if: $includeOffboarding)
				onboardingResponseOverview @include(if: $includeResponseOverview)
				trainingMatrixOverview @include(if: $includeTrainingMatrix)
				onboardingInviteHistory @include(if: $includeOnboardingInviteHistory) {
					...peopleEmployeeOnboardingInviteHistory
				}
				emailFollowUpHistory @include(if: $includeEmailFollowUpHistory) {
					...peopleEmployeeEmailFollowUpHistory
				}
				applicationDocuments @include(if: $includeOnboarding) {
					id
					category
					title
					created
					files {
						key
						s3Key
						mimeType
						sizeBytes
					}
				}
			}
			count
		}
	}
	${PEOPLE_EMPLOYEE_LIST_FRAGMENT}
	${PEOPLE_EMPLOYEE_ONBOARDING_INVITE_HISTORY_FRAGMENT}
	${PEOPLE_EMPLOYEE_EMAIL_FOLLOW_UP_HISTORY_FRAGMENT}
`;

export const GET_EMPLOYEES_COUNT = gql`
	query($filter: EmployeeDirectoryFilter) {
		countEmployees(filter: $filter)
	}
`;

export const GET_EMPLOYEE = gql`
	query($id: ID!) {
		getEmployee(id: $id) {
			...peopleEmployeeDetail
			onboardingInviteHistory {
				...peopleEmployeeOnboardingInviteHistory
			}
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
	${PEOPLE_EMPLOYEE_ONBOARDING_INVITE_HISTORY_FRAGMENT}
`;

// Requests every summary field the detail query reads so the cached summaries stay complete.
export const GET_EMPLOYEE_DUE_TASKS = gql`
	query getEmployeeDueTasks($id: ID!) {
		getEmployee(id: $id) {
			id
			actionsSummary {
				level
				intent
				label
				summary
				count
				reasons
				details
				items {
					urgency
					category
					title
					detail
					dueAt
				}
			}
			adminActionsSummary {
				level
				intent
				label
				summary
				count
				reasons
				details
			}
			emailFollowUpHistory {
				...peopleEmployeeEmailFollowUpHistory
			}
		}
	}
	${PEOPLE_EMPLOYEE_EMAIL_FOLLOW_UP_HISTORY_FRAGMENT}
`;

export const GET_EMPLOYEE_EMPLOYMENT_TYPE = gql`
	query getEmployeeEmploymentType($id: ID!) {
		getEmployee(id: $id) {
			id
			employmentType
			workDays
			workTenant {
				id
			}
		}
	}
`;

export const GET_EMPLOYEE_LEAVE_BALANCES = gql`
	query getEmployeeLeaveBalances($id: ID) {
		getEmployeeLeaveBalances(id: $id) {
			leaveCategoryCode
			leaveName
			numberOfUnits
			typeOfUnits
		}
	}
`;

export const GET_MY_EMPLOYEE_PROFILE = gql`
	query {
		getMyEmployeeProfile {
			...peopleEmployeeDetail
			emailSignatureSignOffEligible
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
`;

export const CREATE_EMPLOYEE = gql`
	mutation createEmployee($input: CreateEmployeeInput!) {
		createEmployee(input: $input) {
			...peopleEmployeeDetail
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
`;

export const MODIFY_EMPLOYEE = gql`
	mutation modifyEmployee($input: ModifyEmployeeInput!) {
		modifyEmployee(input: $input) {
			employee {
				...peopleEmployeeDetail
			}
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
`;

export const CREATE_EMPLOYEE_DEPUTY_EMPLOYEE = gql`
	mutation createEmployeeDeputyEmployee($input: CreateEmployeeDeputyEmployeeInput!) {
		createEmployeeDeputyEmployee(input: $input) {
			...peopleEmployeeDetail
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
`;

export const SET_EMPLOYEE_DEPUTY_SETUP = gql`
	mutation setEmployeeDeputySetup($input: SetEmployeeDeputySetupInput!) {
		setEmployeeDeputySetup(input: $input) {
			...peopleEmployeeDetail
		}
	}
	${PEOPLE_EMPLOYEE_DETAIL_FRAGMENT}
`;

export const ARCHIVE_EMPLOYEE = gql`
	mutation archiveEmployee($id: ID!) {
		archiveEmployee(id: $id)
	}
`;
